/**
 * Email Builder Types
 * Core data structures for the drag-and-drop email builder
 */

// =============================================================================
// Block Types
// =============================================================================

export type BlockType = 'text' | 'image' | 'button' | 'divider' | 'spacer' | 'footer' | 'vaccine_table';

export interface BaseBlock {
  id: string;
  type: BlockType;
}

export type HeadingLevel = 'p' | 'h1' | 'h2' | 'h3' | 'h4';
export type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold';
export type FontFamily = 'Arial' | 'Georgia' | 'Times New Roman' | 'Verdana' | 'Trebuchet MS' | 'Courier New';

export interface TextBlock extends BaseBlock {
  type: 'text';
  content: string;
  headingLevel: HeadingLevel;
  fontFamily: FontFamily;
  fontSize: number;
  fontWeight: FontWeight;
  textAlign: 'left' | 'center' | 'right';
  color: string;
  linkColor: string;
  lineHeight: number;
  letterSpacing: number;
  backgroundColor?: string;
}

export type SizeUnit = 'px' | '%';

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt: string;
  widthValue: number;
  widthUnit: SizeUnit;
  heightValue: number | null; // null = auto
  heightUnit: SizeUnit;
  align: 'left' | 'center' | 'right';
  link?: string;
  backgroundColor?: string;
}

export interface ButtonBlock extends BaseBlock {
  type: 'button';
  text: string;
  link: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  padding: { vertical: number; horizontal: number };
  align: 'left' | 'center' | 'right';
}

export interface DividerBlock extends BaseBlock {
  type: 'divider';
  style: 'solid' | 'dashed' | 'dotted';
  color: string;
  thickness: number;
  width: number; // percentage
  backgroundColor?: string;
}

export interface SpacerBlock extends BaseBlock {
  type: 'spacer';
  height: number;
  backgroundColor?: string;
}

export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
}

export interface FooterBlock extends BaseBlock {
  type: 'footer';
  logoUrl?: string;
  logoWidth: number;
  logoAsAvatar?: boolean;
  socialLinks: SocialLinks;
  showSocialIcons: boolean;
  unsubscribeText: string;
  unsubscribeUrl: string;
  addressText: string;
  contactEmail?: string;
  contactPhone?: string;
  legalText?: string;
  backgroundColor: string;
  textColor: string;
  linkColor: string;
  // Text formatting
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
}

export type VaccineListType = 'overdue' | 'scheduled' | 'all';

export interface VaccineTableBlock extends BaseBlock {
  type: 'vaccine_table';
  title: string;
  vaccineListType: VaccineListType;  // Which vaccines to show: overdue, scheduled, or all
  showPetName: boolean;
  showVaccineName: boolean;
  showScheduledDate: boolean;
  showDaysOverdue: boolean;
  showStatus: boolean;  // Show vaccine status column (for 'all' and 'scheduled' types)
  headerBackgroundColor: string;
  headerTextColor: string;
  rowBackgroundColor: string;
  alternateRowColor: string;
  textColor: string;
  borderColor: string;
  overdueTextColor: string;  // Color for days overdue text
  fontFamily?: string;
  fontSize?: number;
}

export type ContentBlock = TextBlock | ImageBlock | ButtonBlock | DividerBlock | SpacerBlock | FooterBlock | VaccineTableBlock;

// =============================================================================
// Row & Column Types
// =============================================================================

export type RowLayout = '1-col' | '2-col' | '3-col' | '1-2' | '2-1';

export interface EmailColumn {
  id: string;
  blocks: ContentBlock[];
  width: number; // percentage (e.g., 50 for 50%)
}

export type BackgroundSize = 'cover' | 'contain' | 'auto' | '100% 100%';
export type BackgroundPosition = 'center' | 'top' | 'bottom' | 'left' | 'right' | 'top left' | 'top right' | 'bottom left' | 'bottom right';
export type BackgroundRepeat = 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';

export interface EmailRow {
  id: string;
  layout: RowLayout;
  columns: EmailColumn[];
  backgroundColor: string;
  padding: number;
  // Background image properties
  backgroundImage?: string;
  backgroundSize?: BackgroundSize;
  backgroundPosition?: BackgroundPosition;
  backgroundRepeat?: BackgroundRepeat;
}

// =============================================================================
// Document Types
// =============================================================================

export interface GlobalStyles {
  backgroundColor: string;
  fontFamily: string;
  contentWidth: number;
}

export interface EmailDocument {
  rows: EmailRow[];
  globalStyles: GlobalStyles;
}

// =============================================================================
// Selection Types
// =============================================================================

export type SelectionType = 'row' | 'column' | 'block' | null;

export interface Selection {
  type: SelectionType;
  rowId?: string;
  columnId?: string;
  blockId?: string;
}

// =============================================================================
// Default Values
// =============================================================================

export const DEFAULT_GLOBAL_STYLES: GlobalStyles = {
  backgroundColor: '#f8fafc',
  fontFamily: "'Segoe UI', Arial, sans-serif",
  contentWidth: 600,
};

export const DEFAULT_TEXT_BLOCK: Omit<TextBlock, 'id'> = {
  type: 'text',
  content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
  headingLevel: 'p',
  fontFamily: 'Arial',
  fontSize: 16,
  fontWeight: 'normal',
  textAlign: 'left',
  color: '#1e293b',
  linkColor: '#1e40af',
  lineHeight: 1.6,
  letterSpacing: 0,
};

// Font size presets for heading levels
export const HEADING_FONT_SIZES: Record<HeadingLevel, number> = {
  h1: 32,
  h2: 28,
  h3: 24,
  h4: 20,
  p: 16,
};

// Available font families for email (web-safe)
export const FONT_FAMILIES: { value: FontFamily; label: string }[] = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { value: 'Courier New', label: 'Courier New' },
];

// Font weight options
export const FONT_WEIGHTS: { value: FontWeight; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'medium', label: 'Medium' },
  { value: 'semibold', label: 'Semibold' },
  { value: 'bold', label: 'Bold' },
];

export const DEFAULT_IMAGE_BLOCK: Omit<ImageBlock, 'id'> = {
  type: 'image',
  src: 'https://placehold.co/600x200/e2e8f0/64748b?text=Drop+Image+Here',
  alt: 'Image placeholder',
  widthValue: 100,
  widthUnit: '%',
  heightValue: null,
  heightUnit: 'px',
  align: 'center',
};

export const DEFAULT_BUTTON_BLOCK: Omit<ButtonBlock, 'id'> = {
  type: 'button',
  text: 'Click Here',
  link: 'https://',
  backgroundColor: '#1e40af',
  textColor: '#ffffff',
  borderRadius: 8,
  padding: { vertical: 14, horizontal: 32 },
  align: 'center',
};

export const DEFAULT_DIVIDER_BLOCK: Omit<DividerBlock, 'id'> = {
  type: 'divider',
  style: 'solid',
  color: '#e2e8f0',
  thickness: 1,
  width: 100,
};

export const DEFAULT_SPACER_BLOCK: Omit<SpacerBlock, 'id'> = {
  type: 'spacer',
  height: 24,
};

export const DEFAULT_FOOTER_BLOCK: Omit<FooterBlock, 'id'> = {
  type: 'footer',
  logoUrl: '',
  logoWidth: 120,
  logoAsAvatar: false,
  socialLinks: {},
  showSocialIcons: false,
  unsubscribeText: 'Unsubscribe',
  unsubscribeUrl: '{{unsubscribe_url}}',
  addressText: '{{practice_address}}',
  contactEmail: '{{practice_email}}',
  contactPhone: '{{practice_phone}}',
  legalText: '',
  backgroundColor: '#f8fafc',
  textColor: '#64748b',
  linkColor: '#2563eb',
  fontFamily: 'Arial',
  fontSize: 14,
  fontWeight: 'normal',
};

export const DEFAULT_VACCINE_TABLE_BLOCK: Omit<VaccineTableBlock, 'id'> = {
  type: 'vaccine_table',
  title: 'Vaccines',
  vaccineListType: 'overdue',
  showPetName: true,
  showVaccineName: true,
  showScheduledDate: true,
  showDaysOverdue: true,
  showStatus: false,
  headerBackgroundColor: '#1e40af',
  headerTextColor: '#ffffff',
  rowBackgroundColor: '#ffffff',
  alternateRowColor: '#f8fafc',
  textColor: '#1e293b',
  borderColor: '#e2e8f0',
  overdueTextColor: '#dc2626',
  fontFamily: 'Arial',
  fontSize: 14,
};

// =============================================================================
// Helper Functions
// =============================================================================

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function createRow(layout: RowLayout): EmailRow {
  const columnConfigs: Record<RowLayout, number[]> = {
    '1-col': [100],
    '2-col': [50, 50],
    '3-col': [33.33, 33.34, 33.33],
    '1-2': [33.33, 66.67],
    '2-1': [66.67, 33.33],
  };

  const widths = columnConfigs[layout];
  
  return {
    id: generateId(),
    layout,
    columns: widths.map(width => ({
      id: generateId(),
      blocks: [],
      width,
    })),
    backgroundColor: '#ffffff',
    padding: 20,
  };
}

export function createBlock(type: BlockType): ContentBlock {
  const id = generateId();
  
  switch (type) {
    case 'text':
      return { id, ...DEFAULT_TEXT_BLOCK };
    case 'image':
      return { id, ...DEFAULT_IMAGE_BLOCK };
    case 'button':
      return { id, ...DEFAULT_BUTTON_BLOCK };
    case 'divider':
      return { id, ...DEFAULT_DIVIDER_BLOCK };
    case 'spacer':
      return { id, ...DEFAULT_SPACER_BLOCK };
    case 'footer':
      return { id, ...DEFAULT_FOOTER_BLOCK };
    case 'vaccine_table':
      return { id, ...DEFAULT_VACCINE_TABLE_BLOCK };
  }
}

